import React, { createContext, useContext, useState, useEffect } from 'react';

/**
 * A/B Testing Context
 *
 * Provides global state management for boolean A/B tests that can be toggled
 * via the DevTools component. Tests are persisted to localStorage.
 *
 * HOW TO ADD NEW TESTS:
 * ---------------------
 * 1. Add a new test definition to the AB_TESTS array below:
 *
 *    {
 *      key: 'my_new_feature',           // Unique identifier (use snake_case)
 *      name: 'My New Feature',          // Display name shown in DevTools
 *      description: 'Enable experimental dashboard redesign',  // Brief description
 *      defaultValue: false,             // Default state (true/false)
 *    }
 *
 * 2. The toggle will automatically appear in the DevTools component
 * 3. Test values persist across page refreshes via localStorage
 *
 * HOW TO USE IN COMPONENTS:
 * -------------------------
 * Import the useABTest hook and call getTestValue() with your test key:
 *
 * Example 1: Conditional rendering
 *
 *   import { useABTest } from '../contexts/ABTestContext';
 *
 *   const MyComponent = () => {
 *     const { getTestValue } = useABTest();
 *     const isEnabled = getTestValue('my_new_feature');
 *
 *     if (!isEnabled) {
 *       return null;  // Feature disabled
 *     }
 *
 *     return <NewFeature />;
 *   };
 *
 * Example 2: Conditional styling/behavior
 *
 *   const MyButton = () => {
 *     const { getTestValue } = useABTest();
 *     const useNewStyle = getTestValue('new_button_style');
 *
 *     return (
 *       <button style={{ color: useNewStyle ? 'orange' : 'blue' }}>
 *         Click me
 *       </button>
 *     );
 *   };
 *
 * Example 3: Multiple tests
 *
 *   const Dashboard = () => {
 *     const { getTestValue } = useABTest();
 *     const showNewChart = getTestValue('new_chart_view');
 *     const enableRealTime = getTestValue('real_time_updates');
 *
 *     return (
 *       <div>
 *         {showNewChart ? <NewChart /> : <OldChart />}
 *         {enableRealTime && <LiveIndicator />}
 *       </div>
 *     );
 *   };
 */

export interface ABTest {
  key: string;
  name: string;
  description: string;
  defaultValue: boolean;
}

/** Multi-value (string enum) A/B test. Rendered as a <select> in DevTools. */
export interface ABTestEnum {
  key: string;
  name: string;
  description: string;
  /** All valid option values, shown in the DevTools dropdown. */
  options: string[];
  defaultValue: string;
}

interface ABTestContextType {
  tests: ABTest[];
  getTestValue: (key: string) => boolean;
  setTestValue: (key: string, value: boolean) => void;
  resetTests: () => void;
  enumTests: ABTestEnum[];
  getEnumValue: (key: string) => string;
  setEnumValue: (key: string, value: string) => void;
}

const ABTestContext = createContext<ABTestContextType | undefined>(undefined);

/**
 * Define all A/B tests here
 *
 * Each test will automatically appear in the DevTools component with a toggle switch.
 * Add new tests by adding objects to this array following the ABTest interface.
 */
export const AB_TESTS: ABTest[] = [
  {
    key: 'display_name_source_indicator',
    name: 'Display Name Source Indicator',
    description:
      'Show badge indicating whether product name comes from user edit, Orange Book, or CAS registry',
    defaultValue: true,
  },
  {
    key: 'display_name_source_tooltip',
    name: 'Display Name Source Tooltip',
    description:
      'Show source indicator as tooltip over the name instead of a badge (requires display_name_source_indicator)',
    defaultValue: false,
  },
  {
    key: 'modal_field_grouping',
    name: 'Modal Field Grouping',
    description:
      'Group modal fields by data source (Chemical Identity, Data Sources) instead of field type',
    defaultValue: false,
  },
  {
    key: 'product_modal_tabs',
    name: 'Product Modal Tabs',
    description:
      'Split the product modal into Details and Names tabs, with a full source-tagged name-variant list on Names',
    defaultValue: true,
  },
  {
    key: 'validate_products_devtool',
    name: 'Validate Products Devtool',
    description:
      'Show the admin-only "Validate Products" devtool button/modal, global nav indicator, and full jobs page (temporary rollout gate, stacked on top of permanent admin-only RBAC)',
    defaultValue: false,
  },
  // Add more tests here as needed
];

/**
 * Define all multi-value (enum) A/B tests here.
 *
 * Each test will appear in DevTools as a dropdown <select>.
 * Add new tests by following the ABTestEnum interface.
 */
export const AB_ENUM_TESTS: ABTestEnum[] = [
  // Add enum tests here as needed
];

const STORAGE_KEY = 'lgm_ab_tests';
const ENUM_STORAGE_KEY = 'lgm_ab_enum_tests';

export const ABTestProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [testValues, setTestValues] = useState<Record<string, boolean>>(() => {
    // Build defaults from test definitions
    const defaults = AB_TESTS.reduce(
      (acc, test) => {
        acc[test.key] = test.defaultValue;
        return acc;
      },
      {} as Record<string, boolean>
    );
    // Merge stored overrides on top of defaults so that changing
    // defaultValue in code takes effect for users who haven't explicitly toggled
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        return { ...defaults, ...JSON.parse(stored) };
      } catch {
        // Fall through to defaults
      }
    }
    return defaults;
  });

  const [enumValues, setEnumValues] = useState<Record<string, string>>(() => {
    const defaults = AB_ENUM_TESTS.reduce(
      (acc, test) => {
        acc[test.key] = test.defaultValue;
        return acc;
      },
      {} as Record<string, string>
    );
    const stored = localStorage.getItem(ENUM_STORAGE_KEY);
    if (stored) {
      try {
        return { ...defaults, ...JSON.parse(stored) };
      } catch {
        // Fall through to defaults
      }
    }
    return defaults;
  });

  // Persist only overrides (values differing from defaults) so that
  // changing defaultValue in code takes effect for users who haven't toggled
  useEffect(() => {
    const overrides = AB_TESTS.reduce(
      (acc, test) => {
        if (testValues[test.key] !== test.defaultValue) {
          acc[test.key] = testValues[test.key];
        }
        return acc;
      },
      {} as Record<string, boolean>
    );
    localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
  }, [testValues]);

  // Persist enum overrides (values differing from defaults)
  useEffect(() => {
    const overrides = AB_ENUM_TESTS.reduce(
      (acc, test) => {
        if (enumValues[test.key] !== test.defaultValue) {
          acc[test.key] = enumValues[test.key];
        }
        return acc;
      },
      {} as Record<string, string>
    );
    localStorage.setItem(ENUM_STORAGE_KEY, JSON.stringify(overrides));
  }, [enumValues]);

  const getTestValue = (key: string): boolean => {
    return testValues[key] ?? false;
  };

  const setTestValue = (key: string, value: boolean) => {
    setTestValues(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const getEnumValue = (key: string): string => {
    return (
      enumValues[key] ??
      AB_ENUM_TESTS.find(t => t.key === key)?.defaultValue ??
      ''
    );
  };

  const setEnumValue = (key: string, value: string) => {
    setEnumValues(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetTests = () => {
    const defaults = AB_TESTS.reduce(
      (acc, test) => {
        acc[test.key] = test.defaultValue;
        return acc;
      },
      {} as Record<string, boolean>
    );
    setTestValues(defaults);
    const enumDefaults = AB_ENUM_TESTS.reduce(
      (acc, test) => {
        acc[test.key] = test.defaultValue;
        return acc;
      },
      {} as Record<string, string>
    );
    setEnumValues(enumDefaults);
  };

  return (
    <ABTestContext.Provider
      value={{
        tests: AB_TESTS,
        getTestValue,
        setTestValue,
        resetTests,
        enumTests: AB_ENUM_TESTS,
        getEnumValue,
        setEnumValue,
      }}>
      {children}
    </ABTestContext.Provider>
  );
};

export const useABTest = () => {
  const context = useContext(ABTestContext);
  if (!context) {
    throw new Error('useABTest must be used within an ABTestProvider');
  }
  return context;
};
